import { useRouter } from 'expo-router'
import * as React from 'react'
import { useState } from 'react'
import { GestureResponderEvent } from 'react-native'
import { Dialog, Paragraph } from 'react-native-paper'
import { mvs, ScaledSheet } from 'react-native-size-matters'
import { useDispatch } from 'react-redux'
import IconFolderLarge from '~components/IconFolderLarge'
import { Action, NavigationItem, NavigationItemProps } from '~components/NavigationItem'
import usePermission from '~hooks/usePermission'
import { setTemplate } from '~redux/chat/chatSlice'
import { Role } from '~redux/recent/types'
import { ChatTemplate } from '~types'
import { useAxios } from '~utils/fetch'
import { AlertConfirmation } from './AlertConfirmation'

export interface ClassNavigationItemProps extends Partial<NavigationItemProps> {
  id: string
  name: string
  template?: ChatTemplate
  role: Role
  chats_count?: number
  isDashboard?: boolean
}

export function ClassNavigationItem({
  name,
  actions,
  ...props
}: ClassNavigationItemProps): React.JSX.Element {
  const { id, template, role, chats_count = 0, isDashboard = false } = props
  const router = useRouter()
  const permission_create_chat = usePermission('restrictions_max_chats_per_class', chats_count)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const dispatch = useDispatch()

  const onPress = (event: GestureResponderEvent) => {
    if (props.onPress) {
      props.onPress(event)
    } else {
      // TODO: Consider confirmation?
      if (template) {
        if (permission_create_chat.status === 'allowed') {
          dispatch(setTemplate(template))
          router.navigate({
            pathname: '/folders/new-chat',
            params: {
              folderId: id,
              templateId: template.id,
            },
          })
        } else {
          router.navigate({
            pathname: 'denied',
            params: {
              message: permission_create_chat.message,
            },
          })
        }
      } else {
        router.navigate(`/folders/${id}`)
      }
    }
  }

  const [, execute] = useAxios<never>(
    {
      url: `/api/v1/groups/${id}`,
      method: 'DELETE',
    },
    { manual: true },
  )

  const deleteClass = async () => {
    if (role !== 'teacher') {
      return
    }

    try {
      await execute()
    } catch (error) {
      console.info(error)
    }
  }

  const actualActions: Action[] = actions ?? [
    {
      enabled: role === 'teacher',
      name: 'Edit folder settings',
      icon: ['far', 'cog'],
      action: () => router.navigate(`/folders/${id}/settings`),
    },
    {
      enabled: role === 'teacher',
      name: 'Invite members',
      icon: ['far', 'envelope-open'],
      action: () =>
        router.navigate({
          pathname: `/folders/${id}/settings`,
          params: {
            view: 'members',
            subview: 'create',
          },
        }),
    },
    {
      enabled: !isDashboard && role === 'teacher',
      name: 'Delete Folder',
      icon: ['far', 'trash'],
      destructive: true,
      action: () => {
        setShowDeleteConfirmation(true)
      },
    },
  ]

  return (
    <>
      <NavigationItem
        {...props}
        title={name}
        onPress={onPress}
        actions={actualActions}
        background={
          <IconFolderLarge style={styles.background} width={mvs(75, 0.6)} height={mvs(75, 0.6)} />
        }
      />
      {showDeleteConfirmation && (
        <AlertConfirmation
          visible={showDeleteConfirmation}
          setVisible={setShowDeleteConfirmation}
          confirmAction={deleteClass}
        >
          <Dialog.Title>Delete Folder?</Dialog.Title>
          <Dialog.Content>
            <Paragraph>
              Deleting this folder will remove it from your account and it will no longer be visible
              to anyone else.
            </Paragraph>
          </Dialog.Content>
        </AlertConfirmation>
      )}
    </>
  )
}
const styles = ScaledSheet.create({
  background: {
    alignSelf: 'center',
  },
})
